import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger, useOutsideClickEffect } from '@saviynt/common';
import {
  Badge,
  Box,
  Button,
  ButtonCore,
  ButtonIcon,
  Icon,
  Link,
  MobileDrawer,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  clearAllFiltersAction,
  clearSaviActionsFilterAction,
  clearSmartFiltersAction,
  getCertsFilterData,
  selectAllBulkAction,
  setActiveAdvancedFilters,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setCheckForBulkAction,
  setIndeterminateForBulkAction,
  setIsBulkActionsCompleted,
  setPersonalizationStatus,
  setPersonalizationViewOption,
  setSidePanelVisible,
  setTriggerClearBtnForFilters,
  triggerFilterChange,
  updateCertSmartFilter,
  updatePersonalizationAll,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  ITEMS_API_OFFSET,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
  VIEW_OPTIONS,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import {
  certsFormattedMessage,
  getMessageKey,
  scrollToPosition,
} from '../../../utils/utils';
import { checkAllPersonalizedFiltersExist } from '../RowsFilter';
import { getActiveFiltersForRows } from '../RowsFilter/utils';

import AddFilterDropdown from './AddFilterDropdown';
import ChipFilter from './ChipFilter';
import {
  ADDED_FILTER,
  APPLIED_TO_EO_TYPE_AND_ORDER,
  APPLIED_TO_TYPE_AND_ORDER,
  MANUAL_BTNS_FOR_RESPONSIVE_MENU,
  REMOVED_FILTER,
  SPACE_REQUIRED_FOR_DROPDOWN,
  TRANSITION_PROPERTY_TIMEOUT,
  UNCHANGED,
} from './constant';
import CustomChipFilter from './CustomChipFilter';
import { invertedMessages } from './messages';
import SaviActionsFilters from './SaviActionsFilters';
import SmartFilters from './SmartFilters';
import {
  applyDefaultActiveFilters,
  compareTwoStatesForFilter,
  isPersonalizationSaveEnabled,
  reCreateFilterOptions,
  sanitizeAdvancedFilters,
} from './utils';

import './AdvancedFilters.css';

function AdvancedFilters({
  setShowBadgeOnFilterButton,
  showFilterBar,
  setShowFilterBar,
  filters,
  setFilters,
  appliedFilters,
  setAppliedFilters,
  filterBarRef,
  appliedRowFilters,
  rowFilters,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const l = useInvertedLocalize(invertedMessages);

  const { messages } = useSelector((state) => state?.user);
  const {
    bulkAction: {
      isChecked: isBulkChecked,
      isIndeterminate: isBulkIndeterminate,
    },
    core: { columnNames, isLoading, totalItems, campaignType, configMap },
    global: {
      certKey,
      columnPickerList,
      globalDateFormat,
      mediaQueryValue,
      viewRowsList,
      backNavigation,
    },
    lineActions: { reassignConfigMap },
    personalization: {
      status,
      personalizedData,
      viewOption,
      clearCurrentPersonalization,
    },
    saviCompanion: { isSaviEnabled },
    tabs: { tabData },
    viewControls: { certsFilters, customFilters, triggerClearBtnForFilters },
  } = useSelector((state) => state?.certifications?.details);

  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;
  const [isDropDownMenuOpen, setIsDropDownMenuOpen] = useState(false);
  const [dropdownMenuPositionToRight, setDropDownMenuPositionToRight] =
    useState(true);
  const [isClearBtnDisabled, setIsClearBtnDisabled] = useState(false);
  // Get the applied advacned filter values
  const personalizedAdvancedFilters = personalizedData?.result?.info
    ?.defaultView?.[0]
    ? personalizedData.result.info.defaultView[0].advancedFilters
    : undefined;
  const personalizedRowsFilters = personalizedData?.result?.info
    ?.defaultView?.[0]
    ? personalizedData.result.info.defaultView[0].rowsFilters
    : undefined;
  const personalizationViewOption = viewOption?.view;
  const [personalizedDataLoaded, setPersonalizedDataLoaded] = useState(false);
  const [isSmartFilterApplied, setIsSmartFilterApplied] = useState(false);
  const [isSaviActionsApplied, setIsSaviActionsApplied] = useState(false);

  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;

  const dropDownWrapperRef = useRef('menu');
  const filterListWrapRef = useRef('filterListWrap');

  const advancedFiltersClasses = cx(
    isDropDownMenuOpen && 'AdvancedFilters-filterBar--isDropDownMenuOpen',
    showFilterBar
      ? 'AdvancedFilters-filterBar--isActive'
      : 'AdvancedFilters-filterBar--isDisabled'
  );

  // to check the rows changes
  const isRowsFilterChanged = (appliedRowFiltersParam) => {
    const activeFiltersForRows = getActiveFiltersForRows(rowFilters);

    return (
      activeFiltersForRows.length !== appliedRowFiltersParam.length &&
      appliedRowFiltersParam.length !== 0
    );
  };

  useEffect(() => {
    if (filters.length === 0 || activeTabCount === 0) setShowFilterBar(false);
  }, [activeTabCount]);

  useOutsideClickEffect(dropDownWrapperRef, () => {
    scrollToPosition(filterListWrapRef, 0);
    setIsDropDownMenuOpen(false);
  });

  useEffect(() => {
    if (filterBarRef?.current) {
      // eslint-disable-next-line no-param-reassign
      filterBarRef.current.inert = !showFilterBar;
    }
  }, [showFilterBar, filterBarRef]);

  const getDefaultFilters = () => {
    const sanitizedFilters = sanitizeAdvancedFilters(
      certsFilters,
      { columnPickerList, globalDateFormat },
      intl,
      messages,
      l,
      isEOCampaign,
      configMap?.IS_DEFAULT_COLUMN_LABEL_BASED
    );

    const typesList = Object.keys(columnPickerList)?.sort((a, b) => {
      if (isEOCampaign) {
        return (
          APPLIED_TO_EO_TYPE_AND_ORDER.indexOf(a) -
          APPLIED_TO_EO_TYPE_AND_ORDER.indexOf(b)
        );
      }

      return (
        APPLIED_TO_TYPE_AND_ORDER.indexOf(a) -
        APPLIED_TO_TYPE_AND_ORDER.indexOf(b)
      );
    });

    const types = `${typesList?.map((rowType) => l(rowType)).join(', ')}`;

    const customActionFilterKey = 'CERTIFIED';
    const customActionFilterKeyLabel = 'CERTIFIED_ACTION_FILTERS';
    const customActionFilter = {
      key: customActionFilterKey,
      label: `${
        messages[customActionFilterKeyLabel] ||
        localize(intl, getMessageKey('CERTIFIED_ACTION_FILTERS')) ||
        customActionFilterKeyLabel
      }`,
      isActive: true,
      isDefault: false,
      isCustom: true,
      appliedTo: certsFormattedMessage(l('Applied to: {types}', { types }), {
        types,
      }),
      // TODO: @Jacob, @Sagar, @Shaurabh, CR-17904, Is it okay to have this Fallback while we figure out a way to fix customFilters?.actionfilter in the Jest tests?
      // options: [
      //   ...Object.keys(customFilters?.actionfilter).map((action) => ({
      //     value: action,
      //     label:
      //       messages[customFilters?.actionfilter[action]] ||
      //       localize(
      //         intl,
      //         getMessageKey(customFilters?.actionfilter[action])
      //       ),
      //     isChecked: false,
      //   })),
      // ],
      options: customFilters?.actionfilter
        ? Object.keys(customFilters.actionfilter).map((action) => ({
            value: action,
            label:
              messages[customFilters?.actionfilter[action]] ||
              localize(
                intl,
                getMessageKey(customFilters?.actionfilter[action])
              ),
            isChecked: false,
          }))
        : [], // Fallback to an empty array if actionfilter is missing
    };

    const sanitizedFiltersWithCustomFilters = [
      customActionFilter,
      ...sanitizedFilters,
    ];

    return sanitizedFiltersWithCustomFilters;
  };

  useEffect(() => {
    if (certKey && columnNames) {
      dispatch(getCertsFilterData(certKey));
    }
  }, [certKey, columnNames]);

  useEffect(() => {
    if (certsFilters) {
      setFilters(getDefaultFilters());
    }
  }, [certsFilters]);

  useEffect(() => {
    if (!filterBarRef || !filterBarRef.current) return;

    if (
      Math.abs(
        filterBarRef.current.offsetLeft - dropDownWrapperRef.current.offsetLeft
      ) < SPACE_REQUIRED_FOR_DROPDOWN
    ) {
      setDropDownMenuPositionToRight(false);
    } else {
      setDropDownMenuPositionToRight(true);
    }
  }, [filters]);

  // useEffect(() => {
  //   if (!filterBarRef.current) return;

  //   const updateFocusableElements = () => {
  //     // TODO:FIX button removed in array , it is causing issue checkbox greyed out (auto focus) -
  //     const nodeList = filterBarRef.current.querySelectorAll(
  //       '[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  //     );

  //     const focusableElements = [...nodeList].filter((element) => {
  //       // Check if element has an exact class match
  //       const isExactMatch = EXCLUDED_CLASSES_FOR_ACCESIBILITY.some((cls) =>
  //         element.classList.contains(cls)
  //       );

  //       // Check if the element is inside an excluded class but is not itself that element
  //       const isInsideExcluded = EXCLUDED_CLASSES_FOR_ACCESIBILITY.some(
  //         (cls) =>
  //           element.closest(`.${cls}`) && element.closest(`.${cls}`) !== element
  //       );

  //       return isExactMatch || !isInsideExcluded;
  //     });

  //     focusableElements.forEach((el) => {
  //       if (!showFilterBar) {
  //         // Store previous tabindex if not already stored
  //         if (!el.hasAttribute('data-prev-tabindex')) {
  //           el.setAttribute(
  //             'data-prev-tabindex',
  //             el.getAttribute('tabindex') || '0'
  //           );
  //         }

  //         // eslint-disable-next-line no-param-reassign
  //         el.tabIndex = -1;
  //       } else {
  //         const prevTabIndex = el.getAttribute('data-prev-tabindex');

  //         if (prevTabIndex !== null) {
  //           // eslint-disable-next-line no-param-reassign
  //           el.tabIndex = parseInt(prevTabIndex, 10);
  //           el.removeAttribute('data-prev-tabindex');
  //         } else {
  //           el.removeAttribute('tabindex');
  //         }
  //       }
  //     });
  //   };

  //   updateFocusableElements();

  //   // MutationObserver to detect when elements become visible
  //   const observer = new MutationObserver(() => {
  //     updateFocusableElements();
  //   });

  //   observer.observe(filterBarRef.current, {
  //     childList: true,
  //     subtree: true,
  //     attributes: false,
  //   });

  //   // eslint-disable-next-line consistent-return
  //   return () => observer.disconnect();
  // }, [showFilterBar, filterBarRef]);

  useEffect(() => {
    const handleResize = () => {
      if (
        !filterBarRef ||
        !filterBarRef.current ||
        !dropDownWrapperRef ||
        !dropDownWrapperRef.current
      )
        return;

      if (
        Math.abs(
          filterBarRef.current.offsetLeft -
            dropDownWrapperRef.current.offsetLeft
        ) < SPACE_REQUIRED_FOR_DROPDOWN
      ) {
        setDropDownMenuPositionToRight(false);
      } else {
        setDropDownMenuPositionToRight(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Get updated filter options
  const getUpdatedFilterOptions = (newOptions, oldOptions) => {
    const mergedArray = [...oldOptions, ...newOptions];

    //  Remove duplicates while keeping the `isChecked: true` items
    const uniqueMap = new Map();

    mergedArray.forEach((item) => {
      // If the key is not yet in the map, or if this item has `isChecked: true`
      if (!uniqueMap.has(item.value) || item.isChecked) {
        uniqueMap.set(item.value, item);
      }
    });
    const uniqueArray = Array.from(uniqueMap.values());

    return uniqueArray;
  };

  // // after searching  with text for individual chip and update options data
  // const updateFiltersAfterSearch = (data, filterKey, selectedOptions) => {
  //   const options = reCreateFilterOptions(filterKey, data, {
  //     columnPickerList,
  //     globalDateFormat,
  //   });

  //   setFilters((prev) =>
  //     prev.map((filter) => {
  //       if (filter.key === filterKey) {
  //         const oldOptions = [...selectedOptions];
  //         const mergedOptions = getUpdatedFilterOptions(options, oldOptions);

  //         return {
  //           ...filter,
  //           options: mergedOptions,
  //         };
  //       }

  //       return filter;
  //     })
  //   );
  // };

  const handleFilterUpdate = (
    filterToUpdate,
    latestFilters,
    modifiedFilters,
    alreadyAppliedFilters,
    updatedAppliedFilters
  ) => {
    const tempFilters = latestFilters;
    const tempAppliedFilters = alreadyAppliedFilters;

    // Remove or Add filters
    const addOrRemoveMultipleFilters = () => {
      try {
        dispatch(triggerFilterChange());
      } catch (error) {
        Logger.error('addOrRemoveMultipleFilters error', error);
        setFilters(tempFilters);
        setAppliedFilters(tempAppliedFilters);
      } finally {
        setIsClearBtnDisabled(false);
      }
    };

    dispatch(setSidePanelVisible(false));

    if (isBulkChecked || isBulkIndeterminate) {
      dispatch(setBulkActionBySelectAll(true));

      dispatch(selectAllBulkAction(certKey, true))
        .then(() => {
          dispatch(setCheckForBulkAction(false));
          dispatch(setIndeterminateForBulkAction(false));
        })
        .catch((error) => {
          dispatch(setBulkActionAlert(true, error.message));
        });
    }

    if (filterToUpdate.state !== UNCHANGED) {
      setIsClearBtnDisabled(true);
      setFilters(modifiedFilters);
      setAppliedFilters(updatedAppliedFilters);
      dispatch(setActiveAdvancedFilters(updatedAppliedFilters));

      if (filterToUpdate?.filter.length > 1) {
        // getting unique keys for swithing from personalisation to default view
        const uniqueFilterChipKeys = [
          ...new Set(filterToUpdate.filter.map((item) => item.key)),
        ];

        if (
          filterToUpdate.state === ADDED_FILTER ||
          (filterToUpdate.state === REMOVED_FILTER &&
            uniqueFilterChipKeys.length > 1)
        ) {
          addOrRemoveMultipleFilters();
        } else {
          // clear the filters
          dispatch(triggerFilterChange());
          setIsClearBtnDisabled(false);
        }
      } else {
        const promiseToComplete = isSmartFilterApplied
          ? [
              dispatch(clearSmartFiltersAction(true)),
              dispatch(
                updateCertSmartFilter(
                  {
                    certkey: certKey,
                    clearFilter: 'true',
                  },
                  {
                    ITEMS_API_OFFSET,
                    dontRefreshItems: true,
                  }
                )
              ),
            ]
          : [];

        Promise.all(promiseToComplete)
          .then((response) => {
            if (response) {
              dispatch(triggerFilterChange());
            } else {
              setFilters(tempFilters);
              setAppliedFilters(tempAppliedFilters);
            }
          })
          .catch(() => {
            setFilters(tempFilters);
            setAppliedFilters(tempAppliedFilters);
          })
          .finally(() => {
            setIsClearBtnDisabled(false);
          });
      }
    } else if (!filterToUpdate.filter && viewRowsList.length === 1) {
      // when rows length is 1 and filter is not personalized
      dispatch(triggerFilterChange());
    }
  };

  useEffect(() => {
    dispatch(setActiveAdvancedFilters(appliedFilters));

    if (
      appliedFilters.length > 0 ||
      isSmartFilterApplied ||
      isSaviActionsApplied
    ) {
      setShowBadgeOnFilterButton(true);
    } else {
      setShowBadgeOnFilterButton(false);
    }
  }, [appliedFilters, isSmartFilterApplied, isSaviActionsApplied]);

  const handleMultiMenuChange = (filterKey, selectedOptions) => {
    const updatedAppliedFilters = [];
    const prevFilters = JSON.parse(JSON.stringify(filters));
    const modifiedFilters = prevFilters.map((filter) => {
      if (filter.key === filterKey) {
        return {
          ...filter,
          options: filter.options.map((option) => ({
            ...option,
            isChecked: selectedOptions.some(
              (selectedOption) => selectedOption.value === option.value
            ),
          })),
        };
      }

      return filter;
    });

    modifiedFilters.forEach((filter) => {
      const _selectedOptions = filter.options
        .filter((option) => option.isChecked === true)
        .map((option) => ({
          key: filter.key,
          filterLabel: filter.label,
          ...option,
        }));

      updatedAppliedFilters.push(..._selectedOptions);
    });

    const filterToUpdate = compareTwoStatesForFilter(
      appliedFilters,
      updatedAppliedFilters
    );

    // to enable save button for personalization
    if (
      isPersonalizationSaveEnabled(
        personalizedAdvancedFilters,
        updatedAppliedFilters
      ) ||
      isRowsFilterChanged(appliedRowFilters)
    ) {
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
    } else {
      dispatch(setPersonalizationStatus('EXIST_PERSONALIZATION'));
    }

    handleFilterUpdate(
      filterToUpdate,
      prevFilters,
      modifiedFilters,
      appliedFilters,
      updatedAppliedFilters
    );
  };

  const handleRemoveFilter = (filterKey) => {
    const updatedAppliedFilters = [];
    const prevFilters = JSON.parse(JSON.stringify(filters));
    const modifiedFilters = prevFilters.map((filter) => {
      if (filter.key === filterKey) {
        return {
          ...filter,
          options: filter.options.map((option) => ({
            ...option,
            isChecked: false,
          })),
        };
      }

      return filter;
    });

    modifiedFilters.forEach((filter) => {
      const _selectedOptions = filter.options
        .filter((option) => option.isChecked === true)
        .map((option) => ({
          key: filter.key,
          filterLabel: filter.label,
          ...option,
        }));

      updatedAppliedFilters.push(..._selectedOptions);
    });

    const filterToUpdate = compareTwoStatesForFilter(
      appliedFilters,
      updatedAppliedFilters
    );

    // once filter is removed update the personalisation
    if (
      isPersonalizationSaveEnabled(
        personalizedAdvancedFilters,
        updatedAppliedFilters
      ) ||
      isRowsFilterChanged(appliedRowFilters)
    ) {
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
    } else {
      dispatch(setPersonalizationStatus('EXIST_PERSONALIZATION'));
    }

    handleFilterUpdate(
      filterToUpdate,
      prevFilters,
      modifiedFilters,
      appliedFilters,
      updatedAppliedFilters
    );

    setFilters(
      prevFilters.map((filter) => {
        if (filter.key === filterKey) {
          return {
            ...filter,
            isActive: !filter.isActive || filter.isDefault,
            options: filter.options.map((option) => ({
              ...option,
              isChecked: false,
            })),
          };
        }

        return filter;
      })
    );
  };

  // after searching  with text for individual chip and update options data
  const updateFiltersRecords = (data, filterKey, offset = 0) => {
    const options = reCreateFilterOptions(filterKey, data, {
      columnPickerList,
      globalDateFormat,
    });

    setFilters((prev) =>
      prev.map((filter) => {
        if (filter.key === filterKey) {
          const oldOptions = offset === 0 ? [] : [...filter.options];

          if (filter.options) {
            filter.options.forEach((option) => {
              if (option.isChecked) {
                oldOptions.push(option);
              }
            });
          }

          const mergedOptions = getUpdatedFilterOptions(options, oldOptions);

          return {
            ...filter,
            options: mergedOptions,
          };
        }

        return filter;
      })
    );
  };

  const clearDropDownFilters = () => {
    setFilters((prev) =>
      prev.map((filter) => ({
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          isChecked: false,
        })),
      }))
    );
    setAppliedFilters([]);
    dispatch(clearSaviActionsFilterAction(true));
  };

  const resetFilters = () => {
    dispatch(setSidePanelVisible(false));
    setIsClearBtnDisabled(true);
    dispatch(clearAllFiltersAction(true));
    dispatch(clearSaviActionsFilterAction(true));
    dispatch(setActiveAdvancedFilters([]));

    if (certKey) {
      dispatch(triggerFilterChange());

      clearDropDownFilters();
      setShowBadgeOnFilterButton(false);

      if (
        personalizedAdvancedFilters?.length > 0 ||
        isRowsFilterChanged(appliedRowFilters)
      ) {
        dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      } else {
        dispatch(setPersonalizationStatus('EXIST_PERSONALIZATION'));
      }
    }

    setIsClearBtnDisabled(false);
  };

  useEffect(() => {
    if (!isClearBtnDisabled && triggerClearBtnForFilters) {
      resetFilters();
      dispatch(setTriggerClearBtnForFilters(false));
    }
  }, [triggerClearBtnForFilters]);

  const personalizedMultiMenuChange = (
    originalFilters,
    personalisedFilters,
    isClearCurrentPersonalizationTriggered
  ) => {
    const updatedAppliedFilters = [];

    let modifiedFilters = originalFilters.map((filter) => {
      // save the all personalized values
      personalisedFilters.forEach((personalisedfilter) => {
        if (filter.key === personalisedfilter.key) {
          filter.options.push(personalisedfilter);
        }
      });

      // to keep unique records -CR-18530 changes
      const uniqueOptions = (filter.options || []).reduce((acc, item) => {
        const existingIndex = acc.findIndex((opt) => opt.value === item.value);

        if (existingIndex === -1) {
          acc.push(item);
        } else if (item.isChecked && !acc[existingIndex].isChecked) {
          // prioritize keeping the one with isChecked true
          acc[existingIndex] = item;
        }

        return acc;
      }, []);

      return {
        ...filter,
        options: uniqueOptions.map((option) => ({
          ...option,
          isChecked: personalisedFilters.some(
            (selectedOption) =>
              filter.key === selectedOption.key &&
              selectedOption.value === option.value
          ),
        })),
      };
    });

    if (
      personalisedFilters.length &&
      (isClearCurrentPersonalizationTriggered === 'yes' ||
        status === 'EXIST_PERSONALIZATION') &&
      personalizationViewOption === VIEW_OPTIONS.MY_PERSONALIZED_VIEW
    ) {
      modifiedFilters = modifiedFilters.map((filter) => ({
        ...filter,
        isActive: personalizedAdvancedFilters.some((f) => f.key === filter.key),
      }));

      const activeFilters = modifiedFilters.filter((filter) => filter.isActive);
      const inactiveFilters = modifiedFilters.filter(
        (filter) => !filter.isActive
      );

      // If active filters are fewer than 4
      if (activeFilters.length < 4) {
        const originalActiveFilters = originalFilters.filter(
          (filter) =>
            filter.isActive &&
            !activeFilters.some((af) => af.key === filter.key)
        );

        // Filters to add from the original set (excluding already active ones)
        const additionalFiltersNeeded = 4 - activeFilters.length;
        let additionalFilters = originalActiveFilters.slice(
          0,
          additionalFiltersNeeded
        );

        // to set isChecked for additional filters using personalisation values
        additionalFilters = additionalFilters.map((filter) => ({
          ...filter,
          options: filter.options.map((option) => ({
            ...option,
            isChecked: personalisedFilters.some(
              (selectedOption) =>
                filter.key === selectedOption.key &&
                selectedOption.value === option.value
            ),
          })),
        }));

        const finalizedFilters = [
          ...activeFilters,
          ...additionalFilters.map((filter) => ({ ...filter, isActive: true })),
          ...inactiveFilters.filter(
            (filter) => !additionalFilters.some((af) => af.key === filter.key)
          ),
        ];

        modifiedFilters = finalizedFilters;
      }

      modifiedFilters = applyDefaultActiveFilters(modifiedFilters);
      // Sort so active filters come first
      modifiedFilters = modifiedFilters.sort((a, b) => b.isActive - a.isActive);
    }

    modifiedFilters.forEach((filter) => {
      const _selectedOptions = filter.options
        .filter((option) => option.isChecked === true)
        .map((option) => ({
          key: filter.key,
          filterLabel: filter.label,
          ...option,
        }));

      updatedAppliedFilters.push(..._selectedOptions);
    });

    const filterToUpdate = compareTwoStatesForFilter(
      appliedFilters,
      updatedAppliedFilters
    );

    handleFilterUpdate(
      filterToUpdate,
      originalFilters,
      modifiedFilters,
      appliedFilters,
      updatedAppliedFilters
    );
  };

  useEffect(() => {
    if (filters?.length && personalizedData?.response === 'Success') {
      setPersonalizedDataLoaded(true);
    }
  }, [filters, personalizedAdvancedFilters, personalizedRowsFilters]);

  const onFiltersUpdate = (isClearCurrentPersonalizationTriggered = false) => {
    if (!personalizedDataLoaded) return;

    const applyFilters = (existingFilters, filtersToApply) => {
      personalizedMultiMenuChange(
        existingFilters,
        filtersToApply,
        isClearCurrentPersonalizationTriggered
      );
    };

    const prevFilters = JSON.parse(JSON.stringify(filters));

    if (personalizationViewOption === VIEW_OPTIONS.DEFAULT_VIEW) {
      if (certsFilters) {
        applyFilters(getDefaultFilters(), []); // CR-18530 changes
      } else {
        const filtersWithAllInactive = prevFilters.map((filter) => ({
          ...filter,
          isActive: filter?.key === 'CERTIFIED',
          options: (filter?.options || []).map((option) => ({
            ...option,
            isChecked: false,
          })),
        }));

        applyFilters(filtersWithAllInactive, []);
      }

      setShowBadgeOnFilterButton(false);
    } else {
      const personalizedFiltersExist = checkAllPersonalizedFiltersExist(
        prevFilters,
        personalizedAdvancedFilters
      );

      if (personalizedFiltersExist) {
        applyFilters(prevFilters, personalizedAdvancedFilters);
        dispatch(updatePersonalizationAll({ advancedFilters: true }));
      } else {
        dispatch(
          setPersonalizationViewOption({
            view: VIEW_OPTIONS.DEFAULT_VIEW,
            applied: false,
          })
        );
        dispatch(updatePersonalizationAll({ advancedFilters: false }));
      }
    }
  };

  /**
   * personalizedDataLoaded, personalizedAdvancedFilters,
   * personalizationViewOption dependencies
   *
   */
  useEffect(() => {
    onFiltersUpdate();
  }, [
    personalizedDataLoaded,
    personalizedAdvancedFilters,
    personalizationViewOption,
  ]);

  useEffect(() => {
    if (clearCurrentPersonalization)
      onFiltersUpdate(clearCurrentPersonalization);
  }, [clearCurrentPersonalization]);

  const shouldShowClearAllBtn = Boolean(
    appliedFilters.length > 0 || isSmartFilterApplied || isSaviActionsApplied
  );

  // Additional functionality for devices <600px width
  const [isFirstStep, setIsFirstStep] = useState(true);
  const [step2FilterKey, setStep2FilterKey] = useState('');
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mobileXlargeQuery.value <= mediaQueryValue;

  useEffect(() => {
    setShowFilterBar(false);
    setIsMobileDrawerOpen(false);
    setIsFirstStep(true);
  }, [mediaQueryValue]);

  const mobileDrawerFirstSegmentClass = cx(
    'AdvancedFilters-filterDrawerSegments',
    'AdvancedFilters-filterDrawerSegmentsStep1',
    !isFirstStep && 'AdvancedFilters-filterDrawerSegmentsStep1--transtioned'
  );

  const mobileDrawerSecondSegmentClass = cx(
    'AdvancedFilters-filterDrawerSegments',
    'AdvancedFilters-filterDrawerSegmentsStep2',
    !isFirstStep && 'AdvancedFilters-filterDrawerSegmentsStep2--transtioned'
  );

  const handleFilterDrawerClose = () => {
    setTimeout(() => setIsFirstStep(true), TRANSITION_PROPERTY_TIMEOUT);
    setShowFilterBar(false);
  };

  const handleStep2Configuration = (key) => {
    setStep2FilterKey(key);
    setIsFirstStep(false);
  };

  const renderMobileResponsiveHeaderStep1 = () => (
    <>
      <ButtonIcon
        size='medium'
        kind='ghost'
        ariaLabel={l('Close')}
        icon={<Icon kind={isFirstStep ? 'Close' : 'ChevronLeft'} />}
        onClick={() => handleFilterDrawerClose()}
      />
      <div className='AdvancedFilters-filterDrawerHeaderTitle'>
        <Icon kind='Filter' />
        <Typography kind='h3'>
          {messages.CERTS_FILTER ||
            localize(intl, getMessageKey('CERTS_FILTER'))}
        </Typography>
      </div>
      {/* Clear All Button / Kebab menu for remove and clear action  */}
      <Box tag='div' className='AdvancedFilters-linkBtnWrapper--mobile'>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- Link required as per the design */}
        <Link
          text={l('Clear All')}
          kind='button'
          onClick={() => {
            resetFilters();
            handleFilterDrawerClose();
          }}
          isDisabled={isClearBtnDisabled || !shouldShowClearAllBtn}
        />
      </Box>
    </>
  );

  const renderMobileResponsiveStep1Body = () => (
    <div
      className={
        // eslint-disable-next-line no-restricted-globals
        !isNaN(totalItems)
          ? 'AdvancedFilters-filterDrawerBodyStep1'
          : 'AdvancedFilters-filterDrawerBodyStep1--withFullBodyHeight'
      }>
      {/* Smart and savi filters */}
      <ButtonCore
        className='AdvancedFilters-filterDrawerBodyStep1MenuItems'
        onClick={() =>
          handleStep2Configuration(MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER)
        }
        ariaLabel={
          messages.CERTS_SMART_FILTERS ||
          localize(intl, getMessageKey('CERTS_SMART_FILTERS'))
        }>
        <Typography
          kind={isSmartFilterApplied ? 'body2-bold' : 'body2'}
          mobile={isSmartFilterApplied ? 'body2-bold-m' : 'body2-m'}
          className='AdvancedFilters-filterDrawerBodyStep1MenuItemsText--noFilterApplied'>
          {messages.CERTS_SMART_FILTERS ||
            localize(intl, getMessageKey('CERTS_SMART_FILTERS'))}
          {isSmartFilterApplied && (
            <Badge
              kind='dot'
              color='primary'
              size='small'
              className='AdvancedFilters-filterDrawerBodyStep1MenuItemsTextBadge'
            />
          )}
        </Typography>
        <Icon kind='ChevronRight' size='smallMedium' />
      </ButtonCore>
      {isSaviEnabled &&
        !isSecondaryCertifierLoggedIn &&
        !backNavigation?.consulteeLoggedIn &&
        !isSACampaign && (
          <ButtonCore
            className='AdvancedFilters-filterDrawerBodyStep1MenuItems'
            onClick={() =>
              handleStep2Configuration(
                MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER
              )
            }
            ariaLabel={
              messages.CERTS_SAVI_FILTERS ||
              localize(intl, getMessageKey('CERTS_SAVI_FILTERS'))
            }>
            <Typography
              kind={isSaviActionsApplied ? 'body2-bold' : 'body2'}
              mobile={isSaviActionsApplied ? 'body2-bold-m' : 'body2-m'}
              className='AdvancedFilters-filterDrawerBodyStep1MenuItemsText--noFilterApplied'>
              {messages.CERTS_SAVI_FILTERS ||
                localize(intl, getMessageKey('CERTS_SAVI_FILTERS'))}
              {isSaviActionsApplied && (
                <Badge
                  kind='dot'
                  color='primary'
                  size='small'
                  className='AdvancedFilters-filterDrawerBodyStep1MenuItemsTextBadge'
                />
              )}
            </Typography>
            <Icon kind='ChevronRight' size='smallMedium' />
          </ButtonCore>
        )}
      {/* Regular filters */}
      {filters
        ?.sort((a, b) => b.isCustom - a.isCustom || b.isActive - a.isActive)
        ?.map((filter) => {
          if (filter?.isActive) {
            const countOfCheckedOptions = filter?.options?.filter(
              (option) => option?.isChecked
            )?.length;

            return (
              <ButtonCore
                key={filter.key}
                ariaLabel={filter.label}
                className='AdvancedFilters-filterDrawerBodyStep1MenuItems'
                onClick={() => handleStep2Configuration(filter.key)}>
                {countOfCheckedOptions === 0 && (
                  <>
                    <Typography
                      kind='body2'
                      mobile='body2-m'
                      className='AdvancedFilters-filterDrawerBodyStep1MenuItemsText--noFilterApplied'>
                      {filter.label}
                    </Typography>
                    <Icon kind='ChevronRight' size='smallMedium' />
                  </>
                )}
                {countOfCheckedOptions > 0 && (
                  <>
                    <Box
                      tag='div'
                      className='AdvancedFilters-filterDrawerBodyStep1MenuItemsWrapper'>
                      <Box
                        tag='div'
                        className='AdvancedFilters-filterDrawerBodyStep1MenuItemsWrapper--filterApplied'>
                        <Typography
                          kind='body2-bold'
                          mobile='body2-bold-m'
                          className='AdvancedFilters-filterDrawerBodyStep1MenuItemsText--filterApplied'>
                          {filter.label}
                        </Typography>
                        <Badge
                          kind='number'
                          number={countOfCheckedOptions}
                          color='primary'
                          size='small'
                        />
                      </Box>
                      <Box tag='div'>
                        <Typography
                          kind='body3'
                          mobile='body3-m'
                          className='AdvancedFilters-filterDrawerBodyStep1MenuItemsText--optionsSelected'>
                          {filter?.options
                            ?.filter((option) => option?.isChecked)
                            .map((option) => option?.label)
                            .join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                    <Icon kind='ChevronRight' size='smallMedium' />
                  </>
                )}
              </ButtonCore>
            );
          }

          return null;
        })}
      {/* Button to add the filters */}
      <ButtonCore
        className='AdvancedFilters-filterDrawerBodyStep1MenuItems'
        onClick={() =>
          handleStep2Configuration(MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER)
        }
        ariaLabel={
          messages.CERTS_ADD_FILTER ||
          localize(intl, getMessageKey('CERTS_ADD_FILTER'))
        }>
        <Typography
          kind='body2'
          mobile='body2-m'
          className='AdvancedFilters-filterDrawerBodyStep1MenuItemsText--noFilterApplied'>
          <Icon kind='Add' size='medium' color='navy-900' />
          {messages.CERTS_ADD_FILTER ||
            localize(intl, getMessageKey('CERTS_ADD_FILTER'))}
        </Typography>
      </ButtonCore>
    </div>
  );

  const renderMobileResponsiveHeaderStep2 = () => {
    let targetFilter = filters.find((filter) => filter.key === step2FilterKey);

    if (step2FilterKey === MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER) {
      targetFilter = {
        label:
          messages.CERTS_SMART_FILTERS ||
          localize(intl, getMessageKey('CERTS_SMART_FILTERS')),
        appliedTo: '',
      };
    }

    if (step2FilterKey === MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER) {
      targetFilter = {
        label:
          messages.CERTS_SAVI_FILTERS ||
          localize(intl, getMessageKey('CERTS_SAVI_FILTERS')),
        appliedTo: '',
      };
    }

    const shouldShowKebabMenu = Boolean(
      step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER
    );

    return (
      <>
        <ButtonIcon
          size='medium'
          kind='ghost'
          ariaLabel='Go back'
          icon={<Icon kind='ChevronLeft' />}
          onClick={() => {
            setIsFirstStep(true);
            setStep2FilterKey('');
          }}
        />
        <div
          className={cx(
            shouldShowKebabMenu
              ? 'AdvancedFilters-filterDrawerHeaderTitle--isFocused'
              : 'AdvancedFilters-filterDrawerHeaderTitle'
          )}>
          {shouldShowKebabMenu ? (
            <>
              <Typography kind='h3'>{targetFilter?.label || ' '}</Typography>
              <Typography kind='body3'>
                {targetFilter?.appliedTo.split(':')[0] || ' '}
                {step2FilterKey !==
                  MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER &&
                step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER
                  ? ': '
                  : null}
                <span className='AdvancedFilters-filterDrawerHeaderTitleAppliedTo--categories'>
                  {targetFilter?.appliedTo.split(':')[1] || ' '}
                </span>
              </Typography>
            </>
          ) : (
            <>
              <Icon kind='Add' />
              <Typography kind='h3'>
                {messages.CERTS_ADD_FILTER ||
                  localize(intl, getMessageKey('CERTS_ADD_FILTER'))}
              </Typography>
            </>
          )}
        </div>
        <Box tag='div' className='AdvancedFilters-linkBtnWrapper--mobile'>
          <ButtonIcon
            size='medium'
            kind='ghost'
            onClick={
              shouldShowKebabMenu
                ? () => setIsMobileDrawerOpen(true)
                : () => handleFilterDrawerClose()
            }
            icon={
              shouldShowKebabMenu ? (
                <Icon
                  kind='MoreVertical'
                  color='neutral-1000'
                  size='smallMedium'
                />
              ) : (
                <Icon kind='Close' />
              )
            }
          />
        </Box>
      </>
    );
  };

  const renderMobileResponsiveStep2Body = () => {
    // eslint-disable-next-line no-restricted-globals
    const isMobileResponsiveFooterRendered = !isNaN(totalItems);

    if (step2FilterKey === MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER) {
      return (
        <div className='AdvancedFilters-filterDrawerBodyStep2--withFullBodyHeight'>
          <AddFilterDropdown
            showFilterBar={showFilterBar}
            isDropDownMenuOpen={isDropDownMenuOpen}
            headingDescription={
              messages.CERTS_ADD_OR_REMOVE_FILTERS ||
              localize(intl, getMessageKey('CERTS_ADD_OR_REMOVE_FILTERS'))
            }
            placeholder={l('Search')}
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            dropdownMenuPositionToRight={dropdownMenuPositionToRight}
            filterListWrapRef={filterListWrapRef}
            handleRemoveFilter={handleRemoveFilter}
            isMobileResponsivenessEnabled
          />
        </div>
      );
    }

    if (step2FilterKey === MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER) {
      return (
        <div
          // TODO: use classnames
          className={
            isMobileResponsiveFooterRendered
              ? 'AdvancedFilters-filterDrawerBodyStep2'
              : 'AdvancedFilters-filterDrawerBodyStep2--withFullBodyHeight'
          }>
          <SmartFilters
            handleChange={handleMultiMenuChange}
            setShowBadgeOnFilterButton={setShowBadgeOnFilterButton}
            clearDropDownFilters={clearDropDownFilters}
            setIsSmartFilterApplied={setIsSmartFilterApplied}
            isMobileDrawerOpen={isMobileDrawerOpen}
            setIsMobileDrawerOpen={setIsMobileDrawerOpen}
            isMobileResponsivenessEnabled
          />
        </div>
      );
    }

    if (step2FilterKey === MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER) {
      return (
        <div
          // TODO: use classnames
          className={
            isMobileResponsiveFooterRendered
              ? 'AdvancedFilters-filterDrawerBodyStep2'
              : 'AdvancedFilters-filterDrawerBodyStep2--withFullBodyHeight'
          }>
          <SaviActionsFilters
            setIsSaviActionsApplied={setIsSaviActionsApplied}
            isSmartFilterApplied={isSmartFilterApplied}
            isMobileDrawerOpen={isMobileDrawerOpen}
            setIsMobileDrawerOpen={setIsMobileDrawerOpen}
            isMobileResponsivenessEnabled
          />
        </div>
      );
    }

    if (step2FilterKey !== '') {
      const targetFilter = filters.find(
        (filter) => filter.key === step2FilterKey
      );

      return (
        <div
          // TODO: use classnames
          className={
            isMobileResponsiveFooterRendered
              ? 'AdvancedFilters-filterDrawerBodyStep2'
              : 'AdvancedFilters-filterDrawerBodyStep2--withFullBodyHeight'
          }>
          {targetFilter?.isCustom ? (
            <CustomChipFilter
              filter={targetFilter}
              setFilters={setFilters}
              handleRemoveFilter={handleRemoveFilter}
              setPersonalizationStatus={setPersonalizationStatus}
              handleMultiMenuChange={handleMultiMenuChange}
              countOfCheckedOptions={0}
              isMobileResponsivenessEnabled
            />
          ) : (
            <ChipFilter
              filter={targetFilter}
              handleRemoveFilter={handleRemoveFilter}
              setPersonalizationStatus={setPersonalizationStatus}
              handleMultiMenuChange={handleMultiMenuChange}
              certKey={certKey}
              countOfCheckedOptions={0}
              // updateFiltersAfterSearch={updateFiltersAfterSearch}
              updateFiltersRecords={updateFiltersRecords}
              isFilterOpen={!isFirstStep}
              isMobileResponsivenessEnabled
            />
          )}
        </div>
      );
    }

    return null;
  };

  const renderMobileResponsiveFooter = () => {
    const shouldRenderResponsiveFooter = Boolean(
      // eslint-disable-next-line no-restricted-globals
      !isNaN(totalItems) &&
        step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER
    );

    if (shouldRenderResponsiveFooter) {
      return (
        <div className='AdvancedFilters-filterDrawerFooter'>
          <Button
            type='button'
            kind='filled'
            size='medium'
            isLoading={isClearBtnDisabled || isLoading}
            isDisabled={isClearBtnDisabled || isLoading}
            loaderText={l('Loading')}
            onClick={() => handleFilterDrawerClose()}>
            {certsFormattedMessage(
              l('See Results ({totalItems})', { totalItems }),
              { totalItems }
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  const handleMobileClearBtnClick = (targetFilter) => {
    if (
      !isFirstStep &&
      isMobileDrawerOpen &&
      step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER &&
      step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER &&
      step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER
    ) {
      handleMultiMenuChange(targetFilter.key, []);
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      dispatch(setIsBulkActionsCompleted(false));

      setIsMobileDrawerOpen(false);
    }
  };

  const handleMobileRemoveBtnClick = (targetFilter) => {
    handleRemoveFilter(targetFilter.key);

    setIsMobileDrawerOpen(false);
    setIsFirstStep(true);
  };

  const renderClearBtnsForMobile = () => {
    const shouldRenderClearBtns = Boolean(
      isMobileDrawerOpen &&
        !isFirstStep &&
        step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER &&
        step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER &&
        step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER
    );

    const shouldRenderRemoveBtn = Boolean(
      step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.ADD_FILTER &&
        step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SMART_FILTER &&
        step2FilterKey !== MANUAL_BTNS_FOR_RESPONSIVE_MENU.SAVI_FILTER &&
        step2FilterKey !== 'CERTIFIED'
    );

    const targetFilter = filters.find(
      (filter) => filter.key === step2FilterKey
    );

    const shouldDisableCLearBtn = Boolean(
      !targetFilter?.options?.some((option) => option.isChecked) ||
        isClearBtnDisabled
    );

    return (
      shouldRenderClearBtns && (
        <MobileDrawer
          isOpen={shouldRenderClearBtns}
          setIsOpen={setIsMobileDrawerOpen}
          isSwipeEnabled
          content={
            <>
              {shouldRenderRemoveBtn && (
                <Button
                  type='button'
                  kind='ghost'
                  size='medium'
                  onClick={() => handleMobileRemoveBtnClick(targetFilter)}
                  className='AdvancedFilters-filterDrawerClearAndRemoveBtns'>
                  {l('Remove Filter')}
                </Button>
              )}
              <Button
                type='button'
                kind='ghost'
                size='medium'
                isDisabled={shouldDisableCLearBtn}
                onClick={() => handleMobileClearBtnClick(targetFilter)}
                className='AdvancedFilters-filterDrawerClearAndRemoveBtns'>
                {l('Clear Filter')}
              </Button>
            </>
          }
        />
      )
    );
  };

  // TODO: @Jacob, @Sagar, Needs reworked to fit new MobileDrawer structure
  if (isMobileWidth) {
    return (
      <MobileDrawer
        isOpen={showFilterBar}
        setIsOpen={setShowFilterBar}
        isOutsideClickEnabled={!isMobileDrawerOpen}
        className='AdvancedFilters-filterDrawer'
        headerContent={
          isFirstStep
            ? renderMobileResponsiveHeaderStep1()
            : renderMobileResponsiveHeaderStep2()
        }
        content={
          <>
            <div className={mobileDrawerFirstSegmentClass}>
              {renderMobileResponsiveStep1Body()}
            </div>
            <div className={mobileDrawerSecondSegmentClass}>
              {renderMobileResponsiveStep2Body()}
              {renderClearBtnsForMobile()}
            </div>
          </>
        }
        footerActionBar={renderMobileResponsiveFooter()}
      />
    );
  }
  // Segment ends here

  // For devices above 600px width
  return (
    <div
      id='certsAdvancedFilters'
      className={advancedFiltersClasses}
      ref={filterBarRef}>
      <SmartFilters
        handleChange={handleMultiMenuChange}
        setShowBadgeOnFilterButton={setShowBadgeOnFilterButton}
        clearDropDownFilters={clearDropDownFilters}
        setIsSmartFilterApplied={setIsSmartFilterApplied}
      />
      {isSaviEnabled &&
        !isSecondaryCertifierLoggedIn &&
        !backNavigation?.consulteeLoggedIn &&
        !isSACampaign && (
          <SaviActionsFilters
            setIsSaviActionsApplied={setIsSaviActionsApplied}
            isSmartFilterApplied={isSmartFilterApplied}
          />
        )}
      {/* Custom filters here */}
      {filters
        ?.filter((filter) => filter?.isCustom)
        ?.sort(
          (a, b) =>
            b.isActive - a.isActive ||
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        )
        ?.map((filter) => {
          if (filter?.isActive) {
            const countOfCheckedOptions = filter?.options?.filter(
              (option) => option?.isChecked
            )?.length;

            return (
              <CustomChipFilter
                key={filter.key}
                filter={filter}
                handleRemoveFilter={handleRemoveFilter}
                setPersonalizationStatus={setPersonalizationStatus}
                handleMultiMenuChange={handleMultiMenuChange}
                countOfCheckedOptions={countOfCheckedOptions}
              />
            );
          }

          return null;
        })}
      {/* Filters from filter API */}
      {filters
        ?.filter((filter) => !filter?.isCustom)
        ?.sort((a, b) => b.isActive - a.isActive)
        ?.map((filter) => {
          if (filter?.isActive) {
            const countOfCheckedOptions = filter?.options?.filter(
              (option) => option?.isChecked
            )?.length;

            return (
              <ChipFilter
                key={filter.key}
                filter={filter}
                handleRemoveFilter={handleRemoveFilter}
                setPersonalizationStatus={setPersonalizationStatus}
                handleMultiMenuChange={handleMultiMenuChange}
                certKey={certKey}
                countOfCheckedOptions={countOfCheckedOptions}
                // updateFiltersAfterSearch={updateFiltersAfterSearch}
                updateFiltersRecords={updateFiltersRecords}
              />
            );
          }

          return null;
        })}
      <div
        ref={dropDownWrapperRef}
        className='AdvancedFilters-filterBarDropDownWrap'>
        <ButtonIcon
          size='medium'
          kind='outlinedNeutral'
          isSelected={isDropDownMenuOpen}
          ariaLabel='Add filter'
          icon={<Icon kind='add' color='neutral-1000' />}
          onClick={() => {
            scrollToPosition(filterListWrapRef, 0);
            setIsDropDownMenuOpen(!isDropDownMenuOpen);
          }}
        />
        <AddFilterDropdown
          showFilterBar={showFilterBar}
          isDropDownMenuOpen={isDropDownMenuOpen}
          headingDescription={
            messages.CERTS_ADD_OR_REMOVE_FILTERS ||
            localize(intl, getMessageKey('CERTS_ADD_OR_REMOVE_FILTERS'))
          }
          placeholder={l('Search')}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          dropdownMenuPositionToRight={dropdownMenuPositionToRight}
          dropDownWrapperRef={dropDownWrapperRef}
          filterListWrapRef={filterListWrapRef}
          handleRemoveFilter={handleRemoveFilter}
        />
      </div>
      {shouldShowClearAllBtn && (
        <Box tag='div' className='AdvancedFilters-linkBtnWrapper'>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- Link required as per the design */}
          <Link
            text={l('Clear')}
            kind='button'
            onClick={resetFilters}
            isDisabled={isClearBtnDisabled}
          />
        </Box>
      )}
    </div>
  );
}

AdvancedFilters.propTypes = {
  setShowBadgeOnFilterButton: PropTypes.func.isRequired,
  showFilterBar: PropTypes.bool.isRequired,
  setShowFilterBar: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
  appliedFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  filterBarRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  appliedRowFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  rowFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
AdvancedFilters.defaultProps = { filterBarRef: null };

export default AdvancedFilters;
